import {
  Box,
  Button,
  Collapse,
  Fade,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { useRouter } from "next/router"
import { useEffect, useMemo, useRef, useState } from "react"
import { DocumentModel } from "../../../../../api-clients/product-api-v1"
import { useBrandedTranslation } from "../../../../../common/hooks/useBrandedTranslation"
import { handleDownloadClick } from "../../../../../common/utils/common-utils"
import { extractLanguage } from "../../../../../common/utils/locale-utils"
import FilterableText from "../../../../shared/FilterableText"
import { CDownload, CMinus, CPlus } from "../../../../shared/Icons"
import InfoIcon from "../../../../shared/InfoIcon"
import InputFilter from "../../../../shared/InputFilter"
import { SectionTitle } from "../../../../shared/SectionTitle"
import DocumentLanguages from "./DocumentLanguages"

const columnWidths = {
  description: "72",
  explanation: "auto",
  languages: "40",
  additionalLanguages: "14",
}

const tableHeaderAccessors = {
  description: "description",
  explanation: "explanation",
  languages: "languages",
  additionalLanguages: "additionalLanguages",
}

type Props = {
  documents?: DocumentModel[]
  isExtendedByDefault?: boolean
  hasSectionTitle?: boolean
}

const Documents = ({ documents, isExtendedByDefault = false, hasSectionTitle = true }: Props) => {
  const { locale, query } = useRouter()
  const { productId } = query
  const { t } = useBrandedTranslation()
  const [isExtended, setIsExtended] = useState(isExtendedByDefault)
  const [filterValue, setFilterValue] = useState("")
  const [tableData, setTableData] = useState([])
  const documentsSection = useRef<HTMLElement | null>()
  const currentLanguage = extractLanguage(locale as string).toUpperCase()

  const GTMclassName = "document-download-link"
  const currentLanguageDocs = []
  const otherLanguageDocs = []

  const containsInDocument = (filterVal: string, doc: DocumentModel) => {
    return (
      (doc.description && doc.description.toLowerCase().indexOf(filterVal?.toLowerCase()) !== -1) ||
      (doc.explanation && doc.explanation.toLowerCase().indexOf(filterVal?.toLowerCase()) !== -1)
    )
  }

  documents?.forEach((doc: DocumentModel) => {
    const docLanguages = Array.from(doc.languages)
    if (!docLanguages?.length || docLanguages?.indexOf(currentLanguage) >= 0) {
      currentLanguageDocs.push(doc)
    } else {
      if (filterValue.length > 2) {
        containsInDocument(filterValue, doc) && otherLanguageDocs.push(doc)
      } else {
        otherLanguageDocs.push(doc)
      }
    }
  })

  const handleSparePartTableViewToggle = () => {
    setIsExtended(!isExtended)
    if (isExtended) window.scrollTo({ top: documentsSection?.current?.offsetTop, behavior: "auto" })
  }

  const columns = useMemo(
    () => [
      {
        Header: t("documentsAndVideos-description"),
        accessor: tableHeaderAccessors.description,
      },
      {
        Header: t("documents-docTitle"),
        accessor: tableHeaderAccessors.explanation,
      },
      {
        Header: t("documentsAndVideos-language"),
        accessor: tableHeaderAccessors.languages,
      },
      {
        Header: null, // No header
        accessor: tableHeaderAccessors.additionalLanguages,
      },
    ],

    []
  )

  const data = isExtended ? [].concat(currentLanguageDocs, otherLanguageDocs) : currentLanguageDocs

  useEffect(() => {
    documentsSection.current = document.getElementById("documents")
  }, [])

  /* Reset component state on change of product */
  useEffect(() => {
    setIsExtended(false)
    setFilterValue("")
    setTableData(data)
    // eslint-disable-next-line
  }, [productId])

  useEffect(() => {
    if (filterValue.length > 2) {
      const filteredTableData = data.filter((cell: DocumentModel) => {
        return containsInDocument(filterValue, cell)
      })

      setTableData(filteredTableData)
    } else {
      setTableData(data)
    }
    // eslint-disable-next-line
  }, [filterValue, isExtended])

  return (
    <>
      <Flex
        justifyContent={hasSectionTitle ? "space-between" : "flex-end"}
        flexDirection={{ base: "column", sm: "row" }}
        mb="6"
        alignItems={{ sm: "center" }}
        gap="6"
      >
        {hasSectionTitle && <SectionTitle title={t("pdp-nav-documents")} mb="0" />}
        <InputFilter placeholder={t("filter-documents")} filterValue={filterValue} setFilterValue={setFilterValue} />
      </Flex>

      <Fade in={!tableData?.length && !otherLanguageDocs.length} unmountOnExit>
        <Text fontSize="sm" mt="4">
          {t("pdp-article-filter-no-results")}
        </Text>
      </Fade>

      <Collapse in={!!tableData?.length || !!otherLanguageDocs.length} animateOpacity unmountOnExit>
        <>
          {/* Desktop and Tablet */}
          <TableContainer
            mb={!!otherLanguageDocs?.length ? { base: "6", md: "8" } : "0"}
            whiteSpace="normal"
            display={{ base: "none", sm: "block" }}
          >
            <Table variant="inverseStriped" fontSize="sm">
              <Thead borderBottom="1px" borderColor="gray.300" borderStyle="solid">
                <Tr>
                  {columns?.map((col: { accessor: string; Header: string }) => {
                    return (
                      <Th
                        key={col.accessor}
                        fontSize="sm"
                        px="4"
                        w={columnWidths[col.accessor]}
                        textAlign={col.accessor === tableHeaderAccessors.languages ? "end" : "left"}
                      >
                        {col.Header}
                      </Th>
                    )
                  })}
                </Tr>
              </Thead>

              <Tbody>
                {tableData?.map((cellData: DocumentModel, rowIndex: number) => {
                  const additionalLanguagesLength = cellData[tableHeaderAccessors.languages].length
                  const additionalLanguagesLabel = cellData[tableHeaderAccessors.languages]
                    ?.join(", ")
                    .replace(`${currentLanguage},`, "")

                  return (
                    <Tr key={rowIndex} _hover={{ bgColor: "gray.200", cursor: "pointer" }}>
                      {columns?.map((col, index: number) => {
                        if (col.accessor === tableHeaderAccessors.languages) {
                          return (
                            <Td
                              py="2.5"
                              px="4"
                              maxW="30"
                              key={index}
                              verticalAlign="top"
                              textAlign="end"
                              className={GTMclassName} // GTM Event: Document download
                              onClick={(e) => handleDownloadClick(e, cellData.url)}
                            >
                              <DocumentLanguages languages={cellData[col.accessor]} currentLanguage={currentLanguage} />
                            </Td>
                          )
                        }

                        if (col.accessor === tableHeaderAccessors.additionalLanguages) {
                          return (
                            <Td
                              py="2.5"
                              px="4"
                              maxW="10"
                              key={index}
                              verticalAlign="top"
                              className={additionalLanguagesLength <= 1 ? GTMclassName : ""} // GTM Event: Document download
                              onClick={(e) =>
                                additionalLanguagesLength <= 1 ? handleDownloadClick(e, cellData.url) : {}
                              }
                            >
                              {additionalLanguagesLength > 1 && <InfoIcon tooltipLabel={additionalLanguagesLabel} />}
                            </Td>
                          )
                        }

                        return (
                          <Td
                            py="2.5"
                            px="4"
                            key={index}
                            verticalAlign="top"
                            className={GTMclassName} // GTM Event: Document download
                            onClick={(e) => handleDownloadClick(e, cellData.url)}
                          >
                            {col.accessor === tableHeaderAccessors.description ? (
                              <Flex gap="3">
                                <CDownload />
                                <FilterableText text={cellData[col.accessor] ?? ""} filterValue={filterValue} />
                              </Flex>
                            ) : (
                              <FilterableText text={cellData[col.accessor] ?? ""} filterValue={filterValue} />
                            )}
                          </Td>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {/* Mobile */}
          <Flex
            mb={!!otherLanguageDocs?.length ? "4" : "0"}
            flexDirection="column"
            fontSize="sm"
            display={{ base: "flex", sm: "none" }}
          >
            {tableData?.map((cellData: DocumentModel, rowIndex: number) => {
              const cellDataLanuages = Array.from(cellData.languages)
              const additionalLanguagesLabel = cellDataLanuages?.join(", ").replace(`${currentLanguage},`, "")

              return (
                <Flex
                  key={rowIndex}
                  borderBottom="1px"
                  borderColor="gray.300"
                  borderStyle="solid"
                  pt="5"
                  pb="3"
                  _last={{ borderBottom: "none" }}
                >
                  <Flex flexDirection="column" w="100%">
                    <Button
                      className={GTMclassName} // GTM Event: Document download
                      onClick={(e) => handleDownloadClick(e, cellData.url)}
                      justifyContent="flex-start"
                      paddingStart="2.5"
                      fontWeight="normal"
                      bgColor="transparent"
                      mb="1"
                    >
                      <CDownload mr="3" />
                      <FilterableText text={cellData.description ?? ""} filterValue={filterValue} />
                    </Button>

                    <Box pl="10.5">
                      <FilterableText text={cellData.explanation ?? ""} filterValue={filterValue} />
                    </Box>

                    {!!cellDataLanuages.length && (
                      <Flex pl="10.5" h="10" justifyContent="space-between" alignItems="center">
                        <DocumentLanguages languages={cellDataLanuages} currentLanguage={currentLanguage} />
                        {cellDataLanuages.length > 1 && (
                          <Flex w="10" h="10" justifyContent="center" alignItems="center">
                            <InfoIcon tooltipLabel={additionalLanguagesLabel} />
                          </Flex>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              )
            })}
          </Flex>
        </>

        {!!otherLanguageDocs.length && (
          <Button
            leftIcon={isExtended ? <CMinus /> : <CPlus />}
            border="1px solid"
            variant="secondary"
            color="black"
            w={{ base: "100%", sm: "auto" }}
            onClick={handleSparePartTableViewToggle}
          >
            {t(isExtended ? "documentsAndVideos-showLess" : "documentsAndVideos-showMore")}
          </Button>
        )}
      </Collapse>
    </>
  )
}

export default Documents
